import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Download, ChevronDown, FileSpreadsheet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtEur, STATUSES, BILLINGS, COMMITMENTS, billingPeriodCost, exportSubscriptionsCsv } from "@/lib/api";
import { Panel } from "@/components/common";
import SubscriptionsTable from "@/components/SubscriptionsTable";
import SubscriptionsSummary from "@/components/SubscriptionsSummary";
import ImportSubscriptionsDialog from "@/pages/admin/ImportSubscriptionsDialog";
import ImportExcelDialog from "@/pages/admin/ImportExcelDialog";

const Sel = ({ value, onChange, options, testid, placeholder = "Seleziona" }) => (
  <div className="relative">
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="h-9 w-full appearance-none rounded-md border border-input bg-transparent pl-3 pr-9 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring [&>option]:bg-[#111827] [&>option]:text-slate-100">
      {!value && <option value="" disabled>{placeholder}</option>}
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
    <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 opacity-50" />
  </div>
);

const NumInput = (props) => <Input onFocus={(e) => e.target.select()} {...props} />;
const AUTO_RENEW_OPTS = [["si", "Sì"], ["no", "No"]];
const BILLING_OPTS = Object.entries(BILLINGS);
const COMMITMENT_OPTS = Object.entries(COMMITMENTS);
const STATUS_OPTS = Object.entries(STATUSES).map(([k, v]) => [k, v.label]);

function SubDialog({ sub, pages, onClose, onSaved }) {
  const [f, setF] = useState(sub);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.page_id) return toast.error("Seleziona la pagina");
    const body = { ...f, quantity: Number(f.quantity) || 0, unit_price: Number(f.unit_price) || 0 };
    try {
      if (f.id) await api.put(`/admin/subscriptions/${f.id}`, body);
      else await api.post("/admin/subscriptions", body);
      toast.success("Abbonamento salvato");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const subPages = pages.filter((p) => p.show_subscriptions || p.id === f.page_id);
  const total = (Number(f.unit_price) || 0) * (Number(f.quantity) || 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-[#111827] border-slate-800">
        <DialogHeader><DialogTitle>{f.id ? "Modifica abbonamento" : "Nuovo abbonamento"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Pagina / tipologia *</Label><Sel value={f.page_id} onChange={(v) => setF({ ...f, page_id: v })} options={subPages.map((p) => [p.id, p.title])} testid="sub-page-select" /></div>
          <div className="space-y-1.5"><Label>Nome servizio *</Label><Input required value={f.service_name} onChange={set("service_name")} data-testid="sub-name-input" /></div>
          <div className="space-y-1.5"><Label>Descrizione</Label><Input value={f.description} onChange={set("description")} data-testid="sub-description-input" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Quantità / licenze</Label><NumInput type="number" min="0" value={f.quantity} onChange={set("quantity")} data-testid="sub-quantity-input" /></div>
            <div className="space-y-1.5"><Label>Prezzo unitario (€) per periodo di obbligo</Label><NumInput type="number" step="0.01" min="0" value={f.unit_price} onChange={set("unit_price")} data-testid="sub-unit-price-input" /></div>
            <div className="space-y-1.5"><Label>Canone totale ({COMMITMENTS[f.commitment || f.billing]?.toLowerCase()})</Label>
              <div className="h-10 flex items-center justify-end rounded-md border border-slate-800 bg-slate-900/60 px-3 font-mono text-sm text-slate-100" data-testid="sub-total-preview">{fmtEur(total)}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Rinnovo/Fatturazione automatico</Label><Sel value={f.auto_renew === false ? "no" : "si"} onChange={(v) => setF({ ...f, auto_renew: v === "si" })} options={AUTO_RENEW_OPTS} testid="sub-auto-renew-select" /></div>
            <div className="space-y-1.5"><Label>Fatturazione</Label><Sel value={f.billing} onChange={(v) => setF({ ...f, billing: v })} options={BILLING_OPTS} testid="sub-billing-select" /></div>
            <div className="space-y-1.5"><Label>Obbligo contrattuale</Label><Sel value={f.commitment || f.billing} onChange={(v) => setF({ ...f, commitment: v })} options={COMMITMENT_OPTS} testid="sub-commitment-select" /></div>
          </div>
          <div className="flex items-center justify-between rounded-md border border-blue-500/20 bg-blue-500/5 px-3 py-2 text-sm">
            <span className="text-slate-400">Costo periodo di fatturazione ({BILLINGS[f.billing]?.toLowerCase()})</span>
            <span className="font-mono text-slate-100" data-testid="sub-billing-cost-preview">{fmtEur(billingPeriodCost({ ...f, price: total }))}</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Data inizio</Label><Input type="date" value={f.start_date} onChange={set("start_date")} data-testid="sub-start-input" /></div>
            <div className="space-y-1.5"><Label>Rinnovo Servizio</Label><Input type="date" value={f.renewal_date} onChange={set("renewal_date")} data-testid="sub-renewal-input" /></div>
            <div className="space-y-1.5"><Label>Stato</Label><Sel value={f.status} onChange={(v) => setF({ ...f, status: v })} options={STATUS_OPTS} testid="sub-status-select" /></div>
          </div>
          <DialogFooter><Button type="submit" className="bg-blue-600 hover:bg-blue-500" data-testid="sub-save-button">Salva</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ClientSubscriptions({ clientId, pages, companyName }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importingXls, setImportingXls] = useState(false);
  const load = useCallback(() => api.get(`/admin/clients/${clientId}/subscriptions`).then((r) => setItems(r.data)), [clientId]);
  useEffect(() => {
    load();
  }, [load]);
  const remove = async (s) => {
    if (!window.confirm(`Eliminare "${s.service_name}"?`)) return;
    await api.delete(`/admin/subscriptions/${s.id}`);
    toast.success("Abbonamento eliminato");
    load();
  };
  const blank = { client_id: clientId, page_id: filter !== "all" ? filter : "", service_name: "", description: "", quantity: 1, unit_price: 0, auto_renew: true, billing: "mensile", commitment: "mensile", start_date: "", renewal_date: "", status: "attivo" };
  const shown = filter === "all" ? items : items.filter((s) => s.page_id === filter);
  const exportCsv = () => exportSubscriptionsCsv(clientId, companyName).then(() => toast.success("CSV esportato")).catch((e) => toast.error(errMsg(e)));
  return (
    <Panel title={`Abbonamenti (${shown.length})`} action={
      <div className="flex flex-wrap gap-2">
        <div className="w-52"><Sel value={filter} onChange={setFilter} options={[["all", "Tutte le pagine"], ...pages.map((p) => [p.id, p.title])]} testid="sub-filter-select" /></div>
        <Button variant="outline" onClick={exportCsv} disabled={!items.length} className="border-slate-700 bg-transparent" data-testid="export-subscriptions-button"><Download size={15} className="mr-1" />Esporta CSV</Button>
        <Button variant="outline" onClick={() => setImporting(true)} className="border-slate-700 bg-transparent" data-testid="import-subscriptions-button"><Upload size={15} className="mr-1" />Importa CSV</Button>
        <Button variant="outline" onClick={() => setImportingXls(true)} className="border-emerald-500/40 text-emerald-300 bg-transparent hover:bg-emerald-500/10" data-testid="import-excel-button"><FileSpreadsheet size={15} className="mr-1" />Importa Excel fornitore</Button>
        <Button onClick={() => setEditing(blank)} className="bg-blue-600 hover:bg-blue-500" data-testid="add-subscription-button"><Plus size={15} className="mr-1" />Aggiungi</Button>
      </div>}>
      <SubscriptionsTable items={shown} pages={pages} onEdit={setEditing} onDelete={remove} />
      <SubscriptionsSummary items={shown} />
      {editing && <SubDialog sub={editing} pages={pages} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {importing && <ImportSubscriptionsDialog clientId={clientId} onClose={() => setImporting(false)} onImported={() => { setImporting(false); load(); }} />}
      {importingXls && <ImportExcelDialog clientId={clientId} pages={pages} onClose={() => setImportingXls(false)} onImported={() => { setImportingXls(false); load(); }} />}
    </Panel>
  );
}
